/**
 * ============================================================================
 * quickMatchService.ts - QUICK MATCH SERVICE
 * ============================================================================
 * 
 * This service finds the best table for a user to join with one tap.
 * Priority order:
 * 1. Tables where friends are playing (with open seats)
 * 2. Featured tables with players (with open seats)  
 * 3. Featured tables that are empty
 * 4. Any public table with open seats
 * 
 * ============================================================================
 */

import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
  limit,
} from 'firebase/firestore';
import { db } from '../firebase';
import { getFriends } from './socialService';
import {
  getFeaturedTables,
  FeaturedTable,
  hasOpenSeats,
  getAllFeaturedTableIds,
} from './featuredTablesService';
import { doc, getDoc } from 'firebase/firestore';
import walletService from './walletService';

// ============================================================================
// TYPES
// ============================================================================

export interface QuickMatchResult {
  success: boolean;
  tableId: string | null;
  tableName: string | null;
  matchType: 'friend' | 'featured_active' | 'featured_empty' | 'public' | 'none';
  reason: string;
  friendUsername?: string;
  currency?: 'playcoins' | 'pkcash';
  blinds?: string;
  playerCount?: number;
  maxSeats?: number;
}

export interface QuickMatchOptions {
  preferredCurrency?: 'playcoins' | 'pkcash';
  maxBuyIn?: number;
  excludeTableIds?: string[];
}

// ============================================================================
// QUICK MATCH LOGIC
// ============================================================================

/**
 * Find the best table for quick match
 * 
 * Priority:
 * 1. Friend tables with open seats
 * 2. Featured tables with players
 * 3. Empty featured tables
 * 4. Any public table with open seats
 * 
 * @param userId - Current user ID
 * @param options - Optional filters
 * @returns QuickMatchResult with table info
 */
// A hung query must never hang Quick Match: every lookup below is
// time-boxed, and the direct featured fallback always gets its shot.
function withTimeout<T>(promise: Promise<T>, ms: number, fallback: T): Promise<T> {
  return Promise.race([
    promise.catch(() => fallback),
    new Promise<T>(resolve => setTimeout(() => resolve(fallback), ms)),
  ]);
}

export async function findQuickMatchTable(
  userId: string,
  options: QuickMatchOptions = {}
): Promise<QuickMatchResult> {
  const { preferredCurrency, maxBuyIn, excludeTableIds = [] } = options;
  
  console.log('🎯 [QuickMatch] Starting search for user:', userId.substring(0, 8));
  
  try {
    // Get user's wallet to check balances
    const wallet = await withTimeout(walletService.getWallet(userId), 3000, null);
    // Wallet timeout: assume affordable - the join itself enforces the
    // real balance, and a stuck wallet read must not block matching.
    const playCoinsBalance = wallet ? (wallet.playCoins || 0) : Number.MAX_SAFE_INTEGER;
    const pkCashBalance = wallet ? (wallet.pkCash || 0) : Number.MAX_SAFE_INTEGER;
    
    console.log('🎯 [QuickMatch] Balances - PlayCoins:', playCoinsBalance, 'PKCash:', pkCashBalance);

    // =========================================================================
    // PRIORITY 1: Check tables where friends are playing
    // =========================================================================
    const friendTable = await withTimeout(findFriendTable(userId, {
      preferredCurrency,
      maxBuyIn,
      excludeTableIds,
      playCoinsBalance,
      pkCashBalance,
    }), 4000, null);
    
    if (friendTable) {
      console.log('🎯 [QuickMatch] Found friend table:', friendTable.tableId);
      return friendTable;
    }

    // =========================================================================
    // PRIORITY 2 & 3: Check featured tables (with players first, then empty)
    // =========================================================================
    const featuredTable = await withTimeout(findFeaturedTable({
      preferredCurrency,
      excludeTableIds,
      playCoinsBalance,
      pkCashBalance,
    }), 4000, null);
    
    if (featuredTable) {
      console.log('🎯 [QuickMatch] Found featured table:', featuredTable.tableId);
      return featuredTable;
    }

    // =========================================================================
    // PRIORITY 4: Check any public table
    // =========================================================================
    const publicTable = await withTimeout(findPublicTable({
      preferredCurrency,
      maxBuyIn,
      excludeTableIds,
      playCoinsBalance,
      pkCashBalance,
    }), 4000, null);
    
    if (publicTable) {
      console.log('🎯 [QuickMatch] Found public table:', publicTable.tableId);
      return publicTable;
    }

    // =========================================================================
    // LAST RESORT: direct fetch of the known featured bot tables by id.
    // The featured tables ALWAYS exist (bots keep them alive), so Quick
    // Match ending with "no tables" means a query above failed - not that
    // the felt is empty. This path skips queries entirely.
    // =========================================================================
    for (const fid of getAllFeaturedTableIds()) {
      if (excludeTableIds.includes(fid)) continue;
      try {
        const snap = await getDoc(doc(db, 'tables', fid));
        if (!snap.exists()) continue;
        const t: any = snap.data();
        const seats = t.maxSeats || 8;
        if ((t.players || []).length >= seats) continue;
        const currency = t.currency || 'playcoins';
        if (preferredCurrency && currency !== preferredCurrency) continue;
        const minBuy = t.minBuyIn || t.buyIn || 0;
        const balance = currency === 'pkcash' ? pkCashBalance : playCoinsBalance;
        if (balance < minBuy) continue;
        console.log('🎯 [QuickMatch] Direct featured fallback:', fid);
        return {
          success: true,
          tableId: fid,
          tableName: t.name || 'Featured Table',
          matchType: (t.players || []).length > 0 ? 'featured_active' : 'featured_empty',
          reason: 'Seated at a featured table',
          currency,
          playerCount: (t.players || []).length,
          maxSeats: seats,
        };
      } catch { /* try the next id */ }
    }

    // No suitable table found
    console.log('🎯 [QuickMatch] No suitable table found');
    return {
      success: false,
      tableId: null,
      tableName: null,
      matchType: 'none',
      reason: 'No tables available. Try creating a new table!',
    };

  } catch (error) {
    console.error('❌ [QuickMatch] Error:', error);
    return {
      success: false,
      tableId: null,
      tableName: null,
      matchType: 'none',
      reason: 'Error finding tables. Please try again.',
    };
  }
}

// ============================================================================
// HELPER: Find table where friends are playing
// ============================================================================

async function findFriendTable(
  userId: string,
  options: {
    preferredCurrency?: 'playcoins' | 'pkcash';
    maxBuyIn?: number;
    excludeTableIds: string[];
    playCoinsBalance: number;
    pkCashBalance: number;
  }
): Promise<QuickMatchResult | null> {
  try {
    // Get user's friends
    const friends = await getFriends(userId);
    
    if (!friends || friends.length === 0) {
      console.log('🎯 [QuickMatch] No friends found');
      return null;
    }
    
    const friendIds = friends.map(f => f.odid);
    console.log('🎯 [QuickMatch] Checking', friendIds.length, 'friends');

    // Query tables where friends are playing
    const tablesRef = collection(db, 'tables');
    const q = query(
      tablesRef,
      where('status', 'in', ['waiting', 'ready', 'playing']),
      where('isPrivate', '==', false),
      orderBy('updatedAt', 'desc'),
      limit(50)
    );

    const snapshot = await getDocs(q);
    
    for (const doc of snapshot.docs) {
      const data = doc.data();
      const tableId = doc.id;
      
      // Skip excluded tables
      if (options.excludeTableIds.includes(tableId)) continue;
      
      // Skip full tables
      const players = data.players || [];
      const maxSeats = data.maxSeats || 8;
      if (players.length >= maxSeats) continue;
      
      // Check if any friend is at this table
      const friendAtTable = players.find((pid: string) => friendIds.includes(pid));
      if (!friendAtTable) continue;
      
      // Check currency preference
      const currency = data.currency || 'playcoins';
      if (options.preferredCurrency && currency !== options.preferredCurrency) continue;
      
      // Check if user can afford the buy-in
      const minBuyIn = data.minBuyIn || 0;
      if (currency === 'pkcash' && options.pkCashBalance < minBuyIn) continue;
      if (currency === 'playcoins' && options.playCoinsBalance < minBuyIn) continue;
      
      // Check max buy-in limit
      if (options.maxBuyIn && minBuyIn > options.maxBuyIn) continue;
      
      // Find friend's username
      const friend = friends.find(f => f.odid === friendAtTable);
      
      return {
        success: true,
        tableId,
        tableName: data.name || 'Poker Table',
        matchType: 'friend',
        reason: `Join ${friend?.username || 'your friend'}'s table`,
        friendUsername: friend?.username,
        currency,
        blinds: `${data.smallBlind}/${data.bigBlind}`,
        playerCount: players.length,
        maxSeats,
      };
    }
    
    return null;
  } catch (error) {
    console.error('❌ [QuickMatch] Error finding friend table:', error);
    return null;
  }
}

// ============================================================================
// HELPER: Find featured table
// ============================================================================

async function findFeaturedTable(
  options: {
    preferredCurrency?: 'playcoins' | 'pkcash';
    excludeTableIds: string[];
    playCoinsBalance: number;
    pkCashBalance: number;
  }
): Promise<QuickMatchResult | null> {
  try {
    const featuredTables = await getFeaturedTables();
    
    if (!featuredTables || featuredTables.length === 0) {
      console.log('🎯 [QuickMatch] No featured tables found');
      return null;
    }

    // Filter by what user can afford and preference
    const affordableTables = featuredTables.filter(table => {
      // Skip excluded
      if (options.excludeTableIds.includes(table.id)) return false;
      
      // Skip full tables
      if (!hasOpenSeats(table)) return false;
      
      // Check currency preference
      if (options.preferredCurrency && table.currency !== options.preferredCurrency) {
        return false;
      }
      
      // Check affordability
      if (table.currency === 'pkcash') {
        return options.pkCashBalance >= table.minBuyIn;
      } else {
        return options.playCoinsBalance >= table.minBuyIn;
      }
    });

    if (affordableTables.length === 0) {
      console.log('🎯 [QuickMatch] No affordable featured tables');
      return null;
    }

    // Sort: tables with players first, then by player count desc
    const sorted = affordableTables.sort((a, b) => {
      // Tables with players come first
      if (a.playerCount > 0 && b.playerCount === 0) return -1;
      if (a.playerCount === 0 && b.playerCount > 0) return 1;
      
      // Sort by player count descending (most active first)
      return b.playerCount - a.playerCount;
    });

    const bestTable = sorted[0];
    const isActive = bestTable.playerCount > 0;

    return {
      success: true,
      tableId: bestTable.id,
      tableName: bestTable.name,
      matchType: isActive ? 'featured_active' : 'featured_empty',
      reason: isActive 
        ? `Join ${bestTable.playerCount} player${bestTable.playerCount > 1 ? 's' : ''} at ${bestTable.tierLabel}`
        : `Start playing at ${bestTable.tierLabel}`,
      currency: bestTable.currency,
      blinds: bestTable.blindsDisplay,
      playerCount: bestTable.playerCount,
      maxSeats: bestTable.maxSeats,
    };

  } catch (error) {
    console.error('❌ [QuickMatch] Error finding featured table:', error);
    return null;
  }
}

// ============================================================================
// HELPER: Find any public table
// ============================================================================

async function findPublicTable(
  options: {
    preferredCurrency?: 'playcoins' | 'pkcash';
    maxBuyIn?: number;
    excludeTableIds: string[];
    playCoinsBalance: number;
    pkCashBalance: number;
  }
): Promise<QuickMatchResult | null> {
  try {
    const tablesRef = collection(db, 'tables');
    
    // Build query - prefer tables with players
    const q = query(
      tablesRef,
      where('status', 'in', ['waiting', 'ready', 'playing']),
      where('isPrivate', '==', false),
      where('isFeatured', '==', false), // Exclude featured (already checked)
      orderBy('updatedAt', 'desc'),
      limit(30)
    );

    const snapshot = await getDocs(q);
    
    // Filter and sort results
    const candidates: Array<{
      id: string;
      data: any;
      playerCount: number;
    }> = [];
    
    for (const doc of snapshot.docs) {
      const data = doc.data();
      const tableId = doc.id;
      
      // Skip excluded tables
      if (options.excludeTableIds.includes(tableId)) continue;
      
      // Skip full tables
      const players = data.players || [];
      const maxSeats = data.maxSeats || 8;
      if (players.length >= maxSeats) continue;
      
      // Check currency preference
      const currency = data.currency || 'playcoins';
      if (options.preferredCurrency && currency !== options.preferredCurrency) continue;
      
      // Check affordability
      const minBuyIn = data.minBuyIn || 0;
      if (currency === 'pkcash' && options.pkCashBalance < minBuyIn) continue;
      if (currency === 'playcoins' && options.playCoinsBalance < minBuyIn) continue;
      
      // Check max buy-in limit
      if (options.maxBuyIn && minBuyIn > options.maxBuyIn) continue;
      
      candidates.push({
        id: tableId,
        data,
        playerCount: players.length,
      });
    }
    
    if (candidates.length === 0) {
      return null;
    }

    // Sort by player count descending
    candidates.sort((a, b) => b.playerCount - a.playerCount);
    
    const best = candidates[0];
    const currency = best.data.currency || 'playcoins';

    return {
      success: true,
      tableId: best.id,
      tableName: best.data.name || 'Poker Table',
      matchType: 'public',
      reason: best.playerCount > 0 
        ? `Join ${best.playerCount} player${best.playerCount > 1 ? 's' : ''}`
        : 'Join open table',
      currency,
      blinds: `${best.data.smallBlind}/${best.data.bigBlind}`,
      playerCount: best.playerCount,
      maxSeats: best.data.maxSeats || 8,
    };

  } catch (error) {
    console.error('❌ [QuickMatch] Error finding public table:', error);
    return null;
  }
}

// ============================================================================
// UTILITY: Get match type display info
// ============================================================================

export function getMatchTypeInfo(matchType: QuickMatchResult['matchType']): {
  icon: string;
  label: string;
  color: string;
} {
  switch (matchType) {
    case 'friend':
      return { icon: '', label: 'Friend\'s Table', color: 'purple.400' };

    case 'featured_active':
      return { icon: '', label: 'Active Table', color: 'green.400' };

    case 'featured_empty':
      return { icon: '', label: 'Quick Play', color: 'cyan.400' };

    case 'public':
      return { icon: '', label: 'Public Table', color: 'blue.400' };

    default:
      return { icon: '', label: 'No Tables', color: 'gray.400' };

  }
}

// ============================================================================
// UTILITY: Check if user can quick match
// ============================================================================

export async function canQuickMatch(userId: string): Promise<{
  canMatch: boolean;
  reason?: string;
}> {
  try {
    const wallet = await walletService.getWallet(userId);
    const playCoinsBalance = wallet?.playCoins || 0;
    const pkCashBalance = wallet?.pkCash || 0;

    // User needs at least enough for the cheapest table
    // PlayCoin Low: 400 min buy-in
    // PKCash Micro: $0.25 min buy-in
    
    if (playCoinsBalance >= 400) {
      return { canMatch: true };
    }
    
    if (pkCashBalance >= 0.25) {
      return { canMatch: true };
    }

    return {
      canMatch: false,
      reason: 'Not enough balance. Get some PlayCoins to play!',
    };

  } catch (error) {
    console.error('❌ [QuickMatch] Error checking eligibility:', error);
    return {
      canMatch: false,
      reason: 'Error checking balance',
    };
  }
}

// ============================================================================
// SERVICE EXPORT
// ============================================================================

const quickMatchService = {
  findQuickMatchTable,
  canQuickMatch,
  getMatchTypeInfo,
};

export default quickMatchService;